import { z } from "zod";

export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 30;
export const USERNAME_PATTERN = /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/;

export const RESERVED_USERNAME_VALUES = [
  "admin",
  "settings",
  "search",
  "login",
  "signup",
  "dashboard",
  "collection",
  "lists",
  "artist",
  "album",
  "release",
  "listings",
  "api",
  "u",
  "profile",
  "help",
  "about",
  "privacy",
  "terms",
  "support",
  "contact",
  "marketplace",
  "sellers",
  "community",
  "forgot-password",
  "reset-password",
] as const;

/** Reserved usernames that conflict with routes or system names. */
export const RESERVED_USERNAMES: ReadonlySet<string> = new Set(RESERVED_USERNAME_VALUES);

export const usernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(USERNAME_MIN_LENGTH)
  .max(USERNAME_MAX_LENGTH)
  .regex(
    USERNAME_PATTERN,
    "Username must be lowercase alphanumeric with hyphens or dots, no leading/trailing separators",
  )
  .refine((value) => !/[-.]{2}/.test(value), "Username cannot contain consecutive separators")
  .refine((value) => !RESERVED_USERNAMES.has(value), "This username is reserved");

/**
 * Sanitizes interactive username input while preserving validation feedback for
 * separator placement and length.
 */
export function sanitizeUsernameInput(input: string): string {
  const raw = input.includes("@") ? extractEmailAlias(input) : input;

  return raw
    .toLowerCase()
    .replace(/[^a-z0-9.-]/g, "")
    .slice(0, USERNAME_MAX_LENGTH);
}

/**
 * Transforms an email alias or display name into a valid username candidate.
 * Pure string transformation — no DB access.
 */
export function cleanToUsername(input: string): string | null {
  const isEmail = input.includes("@");
  const raw = isEmail ? extractEmailAlias(input) : input;

  const cleaned = raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(isEmail ? /[^a-z0-9.]+/g : /[^a-z0-9]+/g, "-")
    .replace(/^[-.]+|[-.]+$/g, "")
    .slice(0, USERNAME_MAX_LENGTH)
    .replace(/[-.]+$/, "");

  if (cleaned.length < USERNAME_MIN_LENGTH) {
    return null;
  }

  return cleaned;
}

function extractEmailAlias(email: string): string {
  const alias = email.split("@")[0] ?? "";
  return alias.split("+")[0] ?? alias;
}
